import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Compass, Search, Scale, GitCompareArrows, Brain, ChevronDown, ChevronUp } from 'lucide-react';

/* Module-level cache — shared with StructuredAnswerCard */
let _cachedIsAdminMP = null;

function useAutoIsAdmin(explicit) {
  const [isAdmin, setIsAdmin] = useState(
    explicit !== undefined ? !!explicit : (_cachedIsAdminMP === true)
  );
  useEffect(() => {
    if (explicit !== undefined) { setIsAdmin(!!explicit); return; }
    if (_cachedIsAdminMP !== null) { setIsAdmin(_cachedIsAdminMP); return; }
    const token = typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('token') : null;
    if (!token) { _cachedIsAdminMP = false; setIsAdmin(false); return; }
    const base = process.env.REACT_APP_BACKEND_URL || '';
    axios.get(`${base}/api/auth/me`)
      .then((r) => { _cachedIsAdminMP = !!(r?.data?.is_admin); setIsAdmin(_cachedIsAdminMP); })
      .catch(() => { _cachedIsAdminMP = false; setIsAdmin(false); });
  }, [explicit]);
  return isAdmin;
}

/**
 * Multi-Path Contract Analysis Engine — UI panels
 *
 * Renders the four evidence-first sections the backend attaches to every
 * AskStewie response:
 *   • DIRECT ANSWER
 *   • WHERE ASKSTEWIE LOOKED
 *   • RELATED CONTRACT LANGUAGE
 *   • CONTRACT vs POLICY
 * Plus an admin-only Reasoning Path diagnostics collapsible.
 */

const TONE = {
  card: {
    background: 'linear-gradient(135deg, rgba(15,27,70,0.85) 0%, rgba(6,17,31,0.9) 100%)',
    border: '1px solid rgba(56,189,248,0.28)',
    borderRadius: 14,
    padding: '18px 20px',
    margin: '14px 0',
    backdropFilter: 'blur(14px) saturate(140%)',
  },
  eyebrow: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
    textTransform: 'uppercase', color: '#38bdf8',
    marginBottom: 10,
  },
  body: { color: '#e6f2ff', fontSize: 14, lineHeight: 1.55 },
  chip: {
    display: 'inline-flex', alignItems: 'center', padding: '3px 9px',
    borderRadius: 999, background: 'rgba(56,189,248,0.10)',
    border: '1px solid rgba(56,189,248,0.35)',
    color: '#7dd3fc', fontSize: 11, fontWeight: 600,
    letterSpacing: '0.04em', marginRight: 6, marginBottom: 6,
  },
  sectionRow: {
    padding: '10px 12px', borderRadius: 10,
    background: 'rgba(6,17,31,0.55)',
    border: '1px solid rgba(56,189,248,0.14)',
    marginBottom: 8,
  },
  meta: { fontSize: 11, color: '#94a3b8', letterSpacing: '0.04em' },
  quote: { fontSize: 13, fontStyle: 'italic', color: '#cbd5e1', marginTop: 6, lineHeight: 1.5 },
};

// ─── DIRECT ANSWER ────────────────────────────────────────────────────
export function DirectAnswerPanel({ direct }) {
  if (!direct) return null;
  return (
    <div style={TONE.card} data-testid="direct-answer-panel">
      <div style={TONE.eyebrow}>
        <Compass size={14} />
        <span>Direct Answer</span>
      </div>
      <div style={TONE.body} data-testid="direct-answer-text">{direct}</div>
    </div>
  );
}

// ─── WHERE ASKSTEWIE LOOKED ───────────────────────────────────────────
export function WhereLookedPanel({ where }) {
  if (!where || (!where.topics_searched?.length && !where.documents_scanned)) {
    return null;
  }
  const labels = where.topic_labels && where.topic_labels.length
    ? where.topic_labels
    : (where.topics_searched || []).map((t) => t.replace(/_/g, ' '));

  return (
    <div style={TONE.card} data-testid="where-looked-panel">
      <div style={TONE.eyebrow}>
        <Search size={14} />
        <span>Where AskStewie Looked</span>
      </div>
      <div style={TONE.body}>
        <div style={{ marginBottom: 8 }} data-testid="where-looked-topic-primary">
          <span style={TONE.meta}>PRIMARY TOPIC</span>
          <div style={{ marginTop: 4, textTransform: 'capitalize', color: '#fff', fontWeight: 500 }}>
            {(where.primary_topic || 'General').replace(/_/g, ' ')}
          </div>
        </div>
        {labels.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <span style={TONE.meta}>TOPICS SEARCHED</span>
            <div style={{ marginTop: 6 }} data-testid="where-looked-topic-chips">
              {labels.map((l, i) => (
                <span key={i} style={TONE.chip} data-testid={`where-looked-chip-${i}`}>{l}</span>
              ))}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <span style={TONE.meta} data-testid="where-looked-docs">
            {where.documents_scanned || 0} documents scanned
          </span>
          <span style={TONE.meta} data-testid="where-looked-chunks">
            {where.chunks_scanned || 0} sections reviewed
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── RELATED CONTRACT LANGUAGE ────────────────────────────────────────
export function RelatedLanguagePanel({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={TONE.card} data-testid="related-language-panel">
      <div style={TONE.eyebrow}>
        <Scale size={14} />
        <span>Related Contract Language</span>
        <span style={{ ...TONE.chip, marginLeft: 'auto', marginRight: 0 }} data-testid="related-language-count">
          {items.length}
        </span>
      </div>
      <div>
        {items.map((r, i) => (
          <div key={i} style={TONE.sectionRow} data-testid={`related-language-item-${i}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>
                  {r.source_title || 'Untitled'}
                </span>
                {r.section_label && (
                  <span style={{ marginLeft: 10, ...TONE.meta }}>
                    {r.section_label}
                  </span>
                )}
              </div>
              {r.topic && (
                <span style={{ ...TONE.chip, marginRight: 0 }} data-testid={`related-language-topic-${i}`}>
                  {String(r.topic).replace(/_/g, ' ')}
                </span>
              )}
            </div>
            {r.snippet && (
              <div style={TONE.quote} data-testid={`related-language-snippet-${i}`}>
                &ldquo;{r.snippet}&rdquo;
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CONTRACT vs POLICY ───────────────────────────────────────────────
export function ContractVsPolicyPanel({ cvp }) {
  if (!cvp) return null;
  const contractFound = cvp?.contract?.found;
  const policyFound = cvp?.policy?.found;
  if (!contractFound && !policyFound) return null;

  const col = (title, tone, quotes, testKey) => (
    <div
      style={{
        flex: 1, minWidth: 240,
        background: 'rgba(6,17,31,0.55)',
        border: `1px solid ${tone}`, borderRadius: 12, padding: 14,
      }}
      data-testid={`cvp-${testKey}-column`}
    >
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: tone, marginBottom: 8,
      }}>
        {title}
      </div>
      {quotes.length === 0 ? (
        <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }} data-testid={`cvp-${testKey}-empty`}>
          No matching {testKey} language retrieved.
        </div>
      ) : quotes.map((q, i) => (
        <div key={i} style={{ marginBottom: 10 }} data-testid={`cvp-${testKey}-quote-${i}`}>
          <div style={{ fontSize: 12, color: '#e6f2ff', fontWeight: 600 }}>{q.source_title}</div>
          {q.section_label && (
            <div style={{ ...TONE.meta, marginTop: 2 }}>{q.section_label}</div>
          )}
          <div style={{ ...TONE.quote, marginTop: 4 }}>&ldquo;{q.snippet}&rdquo;</div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={TONE.card} data-testid="contract-vs-policy-panel">
      <div style={TONE.eyebrow}>
        <GitCompareArrows size={14} />
        <span>Contract vs Company Policy</span>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {col('What the Contract Says', 'rgba(74,222,128,0.55)', cvp?.contract?.quotes || [], 'contract')}
        {col('What Management May Argue', 'rgba(251,146,60,0.55)', cvp?.policy?.quotes || [], 'policy')}
      </div>
      {cvp?.conflicts?.length > 0 && (
        <div style={{
          marginTop: 14, padding: '10px 12px', borderRadius: 10,
          background: 'rgba(251,146,60,0.10)',
          border: '1px solid rgba(251,146,60,0.35)',
          color: '#fed7aa', fontSize: 12, lineHeight: 1.55,
        }} data-testid="cvp-conflicts">
          <strong style={{ letterSpacing: '0.04em' }}>ANALYST NOTE:</strong>{' '}
          {cvp.conflicts.join(' ')}
        </div>
      )}
    </div>
  );
}

// ─── REASONING PATH (Admin Diagnostics) ───────────────────────────────
export function ReasoningPathPanel({ path, isAdmin }) {
  const [open, setOpen] = useState(false);
  if (!isAdmin || !path) return null;

  const decisionColor = {
    direct_match: '#4ade80',
    related_governing_language: '#38bdf8',
    policy_only_no_contract_match: '#fbbf24',
    no_governing_language_found: '#fb7185',
    error: '#fb7185',
  }[path.decision] || '#94a3b8';

  return (
    <div style={TONE.card} data-testid="reasoning-path-panel">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="reasoning-path-toggle"
        style={{
          background: 'transparent', border: 'none', width: '100%',
          padding: 0, cursor: 'pointer', color: '#e6f2ff',
          display: 'flex', alignItems: 'center', gap: 10,
        }}
      >
        <Brain size={14} style={{ color: '#38bdf8' }} />
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: '#38bdf8',
        }}>
          Reasoning Path (Admin)
        </span>
        <span style={{
          marginLeft: 6, padding: '2px 8px', borderRadius: 999,
          background: `${decisionColor}22`, color: decisionColor,
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }} data-testid="reasoning-decision">
          {String(path.decision || 'unknown').replace(/_/g, ' ')}
        </span>
        <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 12, fontSize: 12, color: '#cbd5e1', lineHeight: 1.6 }}>
          <Stage label="Stage 1 — Understand Intent" testId="reasoning-stage1">
            <KV k="Primary issue" v={path?.stage1_intent?.primary_issue || '—'} />
            <KV k="All issues" v={(path?.stage1_intent?.all_issues || []).join(', ') || '—'} />
            <KV k="Mode" v={path?.stage1_intent?.mode || '—'} />
          </Stage>
          <Stage label="Stage 2 — Direct Contract Search" testId="reasoning-stage2">
            <KV k="Hits" v={path?.stage2_direct?.hits ?? 0} />
            <KV k="Top score" v={path?.stage2_direct?.top_score ?? 0} />
            <KV k="Confidence floor" v={path?.stage2_direct?.confidence_floor ?? '—'} />
            <KV k="Sufficient?" v={path?.stage2_direct?.sufficient ? 'YES' : 'NO'} />
          </Stage>
          <Stage label="Stage 3 — Related Governing Language" testId="reasoning-stage3">
            <KV k="Triggered" v={path?.stage3_related?.triggered ? 'YES' : 'NO'} />
            <KV k="Topics searched" v={(path?.stage3_related?.topics_searched || []).join(', ') || '—'} />
            <KV k="Related hits" v={path?.stage3_related?.related_hits ?? 0} />
          </Stage>
          <Stage label="Stage 4 — Contract vs Policy" testId="reasoning-stage4">
            <KV k="Contract hits" v={path?.stage4_synthesis?.contract_hits ?? 0} />
            <KV k="Policy hits" v={path?.stage4_synthesis?.policy_hits ?? 0} />
            <KV k="Conflicts detected" v={path?.stage4_synthesis?.conflicts_detected ? 'YES' : 'NO'} />
          </Stage>
        </div>
      )}
    </div>
  );
}

function Stage({ label, children, testId }) {
  return (
    <div
      style={{
        padding: '10px 12px', borderRadius: 10, marginBottom: 8,
        background: 'rgba(6,17,31,0.55)',
        border: '1px solid rgba(56,189,248,0.14)',
      }}
      data-testid={testId}
    >
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: '#7dd3fc', marginBottom: 6,
      }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 3 }}>
      <span style={{ color: '#94a3b8', minWidth: 140 }}>{k}</span>
      <span style={{ color: '#e6f2ff', fontFamily: 'ui-monospace, monospace' }}>{String(v)}</span>
    </div>
  );
}

// ─── Convenience wrapper — renders the entire 4-panel block ──────────
export default function MultiPathPanels({ answer, isAdmin }) {
  const resolvedAdmin = useAutoIsAdmin(isAdmin);
  if (!answer) return null;
  return (
    <>
      <DirectAnswerPanel direct={answer.direct_answer} />
      <WhereLookedPanel where={answer.where_askstewie_looked} />
      <RelatedLanguagePanel items={answer.related_contract_language} />
      <ContractVsPolicyPanel cvp={answer.contract_vs_policy} />
      <ReasoningPathPanel path={answer.reasoning_path} isAdmin={resolvedAdmin} />
    </>
  );
}
